import React from "react";
import { AlertTriangle, Info } from "lucide-react";

/** Shared building blocks for the Master Admin pages. Dark analytical UI
 *  that stays visually distinct from the consumer LifeGuide app.
 */

export function PageHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8FA894] mb-1">
          {eyebrow}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-white">{title}</h1>
        {subtitle && (
          <p className="text-sm text-[#A9BFA9] mt-1 max-w-2xl">{subtitle}</p>
        )}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

export function SectionHeader({ label, right }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#8FA894] font-semibold">
        {label}
      </h2>
      {right}
    </div>
  );
}

export function Kpi({ label, value, hint, testid, tone = "default" }) {
  const toneCls = {
    default: "bg-[#141B18] border-white/10",
    accent: "bg-[#1B2820] border-[#4F6F52]/40",
    warn: "bg-[#241914] border-[#B4795C]/40",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${toneCls} p-4 min-h-[92px]`}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#8FA894] font-semibold mb-2">
        {label}
      </div>
      <div className="text-2xl font-display text-white">
        {value == null || value === "" ? "—" : value}
      </div>
      {hint && (
        <div className="text-[11px] text-[#7C8F7C] mt-1">{hint}</div>
      )}
    </div>
  );
}

export function AwaitingBanner({ label = "Awaiting analytics integration" }) {
  return (
    <div
      data-testid="awaiting-banner"
      className="rounded-2xl border border-dashed border-white/15 bg-white/5 text-[#B7D2AF] px-4 py-6 text-sm flex items-start gap-3"
    >
      <Info size={16} className="mt-0.5 text-[#B7D2AF]" />
      <div>
        <div className="font-semibold">{label}</div>
        <div className="text-[#8FA894] text-xs mt-1">
          Data will populate here as soon as the integration is wired up.
          No fake values are shown.
        </div>
      </div>
    </div>
  );
}

export function TestDataBanner({ show }) {
  if (!show) return null;
  return (
    <div
      data-testid="test-data-banner"
      className="rounded-2xl border border-[#B4795C]/40 bg-[#241914] text-[#F3D6A6] px-4 py-3 text-sm flex items-start gap-3 mb-6"
    >
      <AlertTriangle size={16} className="mt-0.5" />
      <div>
        <div className="font-semibold">Preview data includes test records</div>
        <div className="text-[#D2B58A] text-xs mt-0.5">
          Toggle "Include test/demo data" off to see clean production-style totals.
        </div>
      </div>
    </div>
  );
}

export function RangeSelect({ value, onChange, options, testid = "admin-range-select" }) {
  const opts = options || [
    ["7d", "7 days"],
    ["30d", "30 days"],
    ["90d", "90 days"],
    ["", "All time"],
  ];
  return (
    <label className="text-xs text-[#A9BFA9] inline-flex items-center gap-2">
      Range
      <select
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#141B18] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white"
      >
        {opts.map(([v, l]) => (
          <option key={v || "all"} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

export function IncludeTestToggle({ value, onChange }) {
  return (
    <label
      data-testid="admin-include-test-toggle"
      className="text-xs text-[#A9BFA9] inline-flex items-center gap-2 select-none cursor-pointer"
    >
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[#4F6F52]"
      />
      Include test/demo data
    </label>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      {...rest}
      className={`rounded-2xl border border-white/10 bg-[#141B18] p-4 sm:p-5 ${className}`}
    >
      {children}
    </div>
  );
}

export function useIncludeTest() {
  const [value, setValue] = React.useState(() => {
    try {
      return localStorage.getItem("lg_admin_include_test") === "1";
    } catch {
      return false;
    }
  });
  const set = React.useCallback((v) => {
    setValue(v);
    try {
      localStorage.setItem("lg_admin_include_test", v ? "1" : "0");
    } catch {}
  }, []);
  return [value, set];
}

export function centsToUsd(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export function pct(v) {
  if (v == null) return "—";
  return `${v.toFixed ? v.toFixed(1) : v}%`;
}
